import { useRef, useState } from "react";
import { OrderStore, SearchApiParams } from "../types";
import { Severity } from "../../../type";
import orderStoreListApi from "../api/orderStoreListApi";

const limit = 10;

/**
 * 店舗用注文履歴一覧取得
 * @param showAlert エラー表示
 */
export default function useOrderStoreList(showAlert: (msg: string, severity?: Severity) => void) {
  const [total, setTotal] = useState(0); // 在庫のレコードの総数
  const [orderList, setOrderList] = useState<OrderStore[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false); // 二重読み込み防止
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false); // スクロール最下層に達した時、値を変更して発火させるイベント
  const [init, setInit] = useState(false);

  const get = async (searchParams: SearchApiParams) => {
    isFastLoading.current = true;
    setIsLoading(true);
    try {
      // offset, limitは必須なのでparamsに付け足す。
      const res = await orderStoreListApi({
        ...searchParams,
        ...{
          offset: 0,
          limit,
        },
      });
      const oList = res.orders;
      setTotal(res.total);
      setOrderList(structuredClone(oList));
      setOffset(oList.length);
    } catch (err) {
      console.error(err);
      showAlert("注文履歴一覧情報の取得に失敗しました。", "error");
    } finally {
      isFastLoading.current = false;
      setIsLoading(false);
    }
  };

  const getMore = async (searchParams: SearchApiParams) => {
    if (orderList.length >= total || !init) {
      isFastLoading.current = false;
      return;
    }

    isFastLoading.current = true;
    setIsMoreLoading(true);
    try {
      const res = await orderStoreListApi({
        limit,
        offset,
        ...searchParams,
      });
      setTotal(res.total);
      setOrderList((prev) => [...prev, ...res.orders]);
      setOffset((prev) => prev + res.orders.length);
    } catch (err) {
      console.error(err);
      showAlert("注文履歴一覧情報の取得に失敗しました。", "error");
    } finally {
      isFastLoading.current = false;
      setIsMoreLoading(false);
    }
  };

  return [
    { total, orderList, isLoading, isMoreLoading, isFastLoading, fireMoreLoadEvent },
    { setOrderList, get, getMore, setFireMoreLoadEvent, setInit, setTotal },
  ] as const;
}
